import { useEffect, useRef } from "react";

const DOT_SPACING = 8;
const DOT_RADIUS = 0.9;
const DOT_GROWTH = 0.55;
const BASE_ALPHA = 0.09;
const PEAK_ALPHA = 0.8;
const PULSE_MS = 4600;
const DRIFT_MS = 11000;
const INTRO_MS = 900;
const ORB_COUNT = 3;
// Dots are batched into this many brightness levels so each frame issues a
// handful of fills instead of one per dot.
const ALPHA_LEVELS = 24;

interface Dot {
    x: number;
    y: number;
    // Fades dots toward the card edges so the field reads as a soft glow.
    edge: number;
    // Per-dot offset so the grid shimmers instead of breathing in lockstep.
    twinkle: number;
}

const smoothstep = (t: number) => t * t * (3 - 2 * t);

// Bloom quickly, linger, then dissolve slowly.
const bloom = (phase: number) => {
    if (phase < 0.35) return smoothstep(phase / 0.35);
    return 1 - smoothstep((phase - 0.35) / 0.65);
};

// Invisible orbs drift across a static dot grid, taking turns to bloom and
// dissolve; each dot's brightness is the sum of the orbs' soft falloff.
// Stable seed from a string so the placeholder and the post-load field line up.
const hashSeed = (value: string) => {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i++) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    hash ^= hash >>> 15;
    hash = Math.imul(hash, 2246822507);
    hash ^= hash >>> 13;
    return ((hash >>> 0) / 4294967296) * 1000;
};

interface ImageDotFieldProps {
    seedKey: string;
    skipIntro?: boolean;
}

export const ImageDotField = ({ seedKey, skipIntro = false }: ImageDotFieldProps) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext("2d");
        if (!canvas || !ctx) return;

        const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        const seed = hashSeed(seedKey);
        const start = performance.now();
        const paths = Array.from({ length: ALPHA_LEVELS }, () => new Path2D());
        let dots: Dot[] = [];
        let width = 0;
        let height = 0;
        let color = "";
        let frame = 0;
        let isVisible = true;

        const readColor = () => {
            color = getComputedStyle(canvas).color;
        };

        const layout = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            width = canvas.clientWidth;
            height = canvas.clientHeight;
            canvas.width = Math.round(width * dpr);
            canvas.height = Math.round(height * dpr);
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            const cols = Math.floor(width / DOT_SPACING);
            const rows = Math.floor(height / DOT_SPACING);
            const offsetX = (width - (cols - 1) * DOT_SPACING) / 2;
            const offsetY = (height - (rows - 1) * DOT_SPACING) / 2;

            dots = [];
            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    const x = offsetX + col * DOT_SPACING;
                    const y = offsetY + row * DOT_SPACING;
                    const nx = Math.min(x, width - x) / (width * 0.18);
                    const ny = Math.min(y, height - y) / (height * 0.18);
                    dots.push({
                        x,
                        y,
                        edge: smoothstep(Math.min(1, nx, ny)),
                        twinkle: Math.sin(col * 12.9898 + row * 78.233 + seed) * 43758.5453 % (Math.PI * 2),
                    });
                }
            }
        };

        const draw = (now: number) => {
            const still = motionQuery.matches;
            // Orbs run on the page clock so a remounted field continues seamlessly.
            const t = still ? PULSE_MS * 0.2 : now;
            const intro = still || skipIntro ? 1 : smoothstep(Math.min(1, (now - start) / INTRO_MS));
            const size = Math.min(width, height);

            const orbs = Array.from({ length: ORB_COUNT }, (_, i) => {
                const phase = ((t / PULSE_MS) + i / ORB_COUNT) % 1;
                const drift = (t / DRIFT_MS) * Math.PI * 2 + seed + i * 2.1;
                const radius = size * (0.2 + 0.16 * phase);
                return {
                    x: width * (0.5 + 0.3 * Math.sin(drift * 0.7 + i)),
                    y: height * (0.5 + 0.3 * Math.cos(drift * 0.5 + i * 1.7)),
                    invRadiusSq: 1 / (radius * radius),
                    strength: (still ? 0.55 : bloom(phase)) * intro,
                };
            });

            for (let i = 0; i < ALPHA_LEVELS; i++) paths[i] = new Path2D();

            const shimmer = t / 900;
            for (const dot of dots) {
                let light = 0;
                for (const orb of orbs) {
                    const dx = dot.x - orb.x;
                    const dy = dot.y - orb.y;
                    light += orb.strength * Math.exp(-(dx * dx + dy * dy) * orb.invRadiusSq);
                }
                light = Math.min(light, 1) * dot.edge;
                light *= 0.9 + 0.1 * Math.sin(shimmer + dot.twinkle);

                const level = Math.min(ALPHA_LEVELS - 1, Math.round(light * (ALPHA_LEVELS - 1)));
                const radius = DOT_RADIUS * (1 + (level / (ALPHA_LEVELS - 1)) * DOT_GROWTH);
                paths[level].moveTo(dot.x + radius, dot.y);
                paths[level].arc(dot.x, dot.y, radius, 0, Math.PI * 2);
            }

            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = color;
            const baseAlpha = BASE_ALPHA * (0.4 + 0.6 * intro);
            for (let i = 0; i < ALPHA_LEVELS; i++) {
                ctx.globalAlpha = baseAlpha + (i / (ALPHA_LEVELS - 1)) * (PEAK_ALPHA - BASE_ALPHA);
                ctx.fill(paths[i]);
            }
            ctx.globalAlpha = 1;
        };

        const loop = (now: number) => {
            draw(now);
            frame = isVisible && !motionQuery.matches ? requestAnimationFrame(loop) : 0;
        };

        const resume = () => {
            if (frame === 0 && isVisible) frame = requestAnimationFrame(loop);
        };

        const resizeObserver = new ResizeObserver(() => {
            layout();
            draw(performance.now());
        });

        // Theme switches change the inherited text colour without resizing.
        const themeObserver = new MutationObserver(() => {
            readColor();
            draw(performance.now());
        });

        const visibilityObserver = new IntersectionObserver(([entry]) => {
            isVisible = entry.isIntersecting;
            resume();
        });

        const handleMotionChange = () => resume();

        readColor();
        layout();
        resizeObserver.observe(canvas);
        visibilityObserver.observe(canvas);
        themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "class"] });
        motionQuery.addEventListener("change", handleMotionChange);
        resume();

        return () => {
            cancelAnimationFrame(frame);
            resizeObserver.disconnect();
            themeObserver.disconnect();
            visibilityObserver.disconnect();
            motionQuery.removeEventListener("change", handleMotionChange);
        };
    }, [seedKey, skipIntro]);

    return <canvas ref={canvasRef} className="image-generation-loading-dots" aria-hidden="true" />;
};
